import boatJson from "../config/boat.json" with { type: "json" };
import botsJson from "../config/bots.json" with { type: "json" };
import fxJson from "../config/fx.json" with { type: "json" };
import generalJson from "../config/general.json" with { type: "json" };
import lootJson from "../config/loot.json" with { type: "json" };
import oceanJson from "../config/ocean.json" with { type: "json" };
import seaLifeJson from "../config/sealife.json" with { type: "json" };
import shotJson from "../config/shot.json" with { type: "json" };
import wakeJson from "../config/wake.json" with { type: "json" };

import { BoatTree, coerceBoatTree } from "./BoatTree";
import { coerceFx, FxTree } from "./Fx";
import {
    BotSettings,
    coerceBots,
    coerceGeneral,
    coerceLoot,
    coerceOcean,
    coerceSeaLife,
    coerceShot,
    coerceWake,
    GeneralSettings,
    LootSettings,
    OceanSettings,
    SeaLifeSettings,
    ShotSettings,
    WakeSettings
} from "./Settings";

/**
 * The stored settings, loaded. This is the one place in the game that touches `config/`;
 * everything else reads the named constants in [Sailing.ts](Sailing.ts) and
 * [Battle.ts](Battle.ts), which are derived from what lands here.
 *
 * The import is a plain JSON module import, which every one of the three toolchains
 * resolves on its own: vite inlines it for the client, `tsx` loads it for the server, and
 * `tsc --noEmit` reads it under `resolveJsonModule`. Nothing reads the file at runtime, so
 * there is no `fs` here and this file stays browser-safe.
 *
 * **It is read once, at module load.** A change made in the editor reaches a running game
 * by restarting it, which both dev processes already do by themselves: `tsx watch` restarts
 * the arena when the JSON changes, and vite reloads the page. Nothing is hot-swapped
 * mid-battle — every boat in a room has to agree about how fast a hull moves, so the arena
 * picking up a new speed under a client that has not would show up as exactly the drift
 * design/Networking.md is built to avoid.
 *
 * `coerceGeneral` runs on the way in, so a hand-edited file that is missing a key, or has a
 * string, or is out of range, degrades to the default for that key instead of putting a
 * `NaN` into the simulation.
 */
export const GENERAL: GeneralSettings = coerceGeneral(generalJson);

/**
 * The loot dials, loaded the same way and read by **both** ends, exactly as `GENERAL` is: the
 * arena mints the stock, spawns it and hands it out, and a client that disagreed about what a
 * coin is worth would draw the wrong score. Its own file because it is its own panel — see
 * design/Settings.md — and not because anything about how it is read differs.
 */
export const LOOT: LootSettings = coerceLoot(lootJson);

/**
 * The water's own dials — the swell, and the texture that scrolls over it. Read by the **client
 * only**, like the school below: the sea is a picture this client draws rather than a field the
 * arena publishes (design/Water.md), so two players may be looking at
 * slightly different water and nothing about the fight changes. That is why these are their own
 * file rather than four more keys in `GENERAL`, which is the arena's contract.
 */
export const OCEAN: OceanSettings = coerceOcean(oceanJson);

/**
 * The sea-life dials, loaded the same way. Read by the client only — the school is decoration
 * with no bearing on the simulation, so unlike `GENERAL` it never reaches the arena and two
 * clients running different values is not a desync. See design/SeaLife.md.
 */
export const SEALIFE: SeaLifeSettings = coerceSeaLife(seaLifeJson);

/**
 * The wake dials, loaded the same way and read by the client only, for the same reason the
 * school is: the trail a hull leaves is a picture of a course the arena has already decided,
 * so two clients drawing different foam is a difference of opinion about water rather than a
 * desync. See design/Water.md.
 */
export const WAKE: WakeSettings = coerceWake(wakeJson);

/**
 * The boat evolution tree, loaded the same way. Read by **both** ends, unlike the school:
 * [Gunnery.ts](Gunnery.ts) turns hull 1's cannon slots and rung 1 of the gun ladder into the
 * guns every captain sails with, and the server fires them. That makes it as sync-critical as
 * `GENERAL` — a client and an arena running different trees would disagree about where a
 * muzzle is and how far its shot carries.
 *
 * Only the parts of the tree the game has grown into are read. The hull ladder, the sails and
 * the milestone curve are still authored and still unused; see design/Evolution.md.
 */
export const BOAT: BoatTree = coerceBoatTree(boatJson);

/**
 * The effects tree, loaded the same way. Read by the **client** only, like the school: a muzzle
 * flash is smoke over a shot the arena has already resolved, so two clients drawing different
 * flames is a difference of opinion about pictures rather than a desync. See design/Settings.md.
 *
 * Nothing reads it yet — the flashes are authored and the code that spawns one at a gun's muzzle
 * is not written. The dashboard's FX panel is where they are fitted.
 */
export const FX: FxTree = coerceFx(fxJson);

/**
 * The shot dials, loaded the same way and read by the **client** only, like the wake. The arc a
 * cannonball appears to fly is drawn over a flight both ends already agree on to the pixel — the
 * arena broadcasts the shot and every client dead-reckons the same straight line — so how big the
 * ball is drawn along the way cannot desync anything, and a ball connects at any point in its
 * curve. See design/Settings.md.
 */
export const SHOT: ShotSettings = coerceShot(shotJson);

/**
 * The bot dials, loaded the same way and read by the **server** only — the mirror of the ocean and
 * the school, which are the client's alone. A bot is a hull in `ArenaState` like any other and
 * every client already draws it like any other, so nothing here has to travel and two ends running
 * different values is not a thing that can happen.
 *
 * **This is the file, not the numbers the arena runs on.** Three of these are bounded by things
 * this module may not know about — the arena's size, its seat cap, its input rate — and two may be
 * overridden from the environment for a test suite or a deployed box. Both of those happen in
 * `server/bots/settings.ts`, which is where a caller should be reading from. See design/Bots.md.
 */
export const BOTS: BotSettings = coerceBots(botsJson);
